import { useEffect, useState, type FormEvent } from "react";
import type { AdminTenantSettingsDto } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminTenantSettings, patchAdminTenantSettings } from "../../shared/api/adminSettings";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const pageLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/public-settings", label: "公開設定" }
];

type TenantSettingsForm = {
  displayName: string;
  brandName: string;
  timezone: string;
  publicStatus: "public" | "private";
  contactEmail: string;
  contactPhone: string;
  websiteUrl: string;
  publicDescription: string;
};

function toForm(settings: AdminTenantSettingsDto): TenantSettingsForm {
  return {
    displayName: settings.displayName,
    brandName: settings.brandName ?? "",
    timezone: settings.timezone,
    publicStatus: settings.publicStatus,
    contactEmail: settings.contactEmail ?? "",
    contactPhone: settings.contactPhone ?? "",
    websiteUrl: settings.websiteUrl ?? "",
    publicDescription: settings.publicDescription ?? ""
  };
}

function renderReadOnlyValue(value: string | null): string {
  return value && value.trim().length > 0 ? value : "-";
}

function describeServiceCategory(value: AdminTenantSettingsDto["serviceCategory"]): string {
  switch (value) {
    case "music":
      return "音楽";
    case "sports":
      return "スポーツ";
    case "academic":
      return "学習";
    case "art":
      return "アート";
    case "other":
      return "その他";
    case "dance":
    default:
      return "ダンス";
  }
}

function describeUiLabelProfile(value: AdminTenantSettingsDto["uiLabelProfile"]): string {
  switch (value) {
    case "genericLesson":
      return "汎用レッスン";
    case "sports":
      return "スポーツ";
    case "music":
      return "音楽";
    case "academic":
      return "学習";
    case "art":
      return "アート";
    case "dance":
    default:
      return "ダンス";
  }
}

export function AdminTenantSettingsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [settings, setSettings] = useState<AdminTenantSettingsDto | null>(null);
  const [form, setForm] = useState<TenantSettingsForm | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [saveState, setSaveState] = useState<"idle" | "saving">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    let cancelled = false;

    async function loadSettings() {
      setLoadState("loading");
      setMessage(null);

      try {
        const nextSettings = await fetchAdminTenantSettings(tenantSlug);

        if (cancelled) {
          return;
        }

        setSettings(nextSettings);
        setForm(toForm(nextSettings));
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setMessage(getProtectedApiErrorMessage(error, "テナント設定の取得に失敗しました"));
      }
    }

    void loadSettings();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!form) {
      return;
    }

    setSaveState("saving");
    setMessage(null);

    try {
      const nextSettings = await patchAdminTenantSettings(tenantSlug, {
        displayName: form.displayName.trim(),
        brandName: form.brandName.trim() || null,
        timezone: form.timezone.trim() || "Asia/Tokyo",
        publicStatus: form.publicStatus,
        contactEmail: form.contactEmail.trim() || null,
        contactPhone: form.contactPhone.trim() || null,
        websiteUrl: form.websiteUrl.trim() || null,
        publicDescription: form.publicDescription.trim() || null
      });
      setSettings(nextSettings);
      setForm(toForm(nextSettings));
      setMessage("テナント設定を保存しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setMessage(getProtectedApiErrorMessage(error, "テナント設定の保存に失敗しました"));
    } finally {
      setSaveState("idle");
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="テナント"
      description="クラブ名と公開向けの基本情報を管理します。"
      links={pageLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p style={{ margin: 0 }}>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p style={{ margin: 0 }}>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p style={{ margin: 0 }}>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p style={{ margin: 0 }}>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p style={{ margin: 0 }}>テナント設定を読み込んでいます。</p> : null}
        {message ? <p style={{ margin: 0 }}>{message}</p> : null}

        {isSessionReady && hasAdminRole && settings && form ? (
          <>
            <form onSubmit={(event) => void handleSubmit(event)} style={{ display: "grid", gap: 16 }}>
              <label style={{ display: "grid", gap: 8 }}>
                <span style={{ fontWeight: 600 }}>表示名</span>
                <input
                  value={form.displayName}
                  onChange={(event) => setForm((current) => current ? { ...current, displayName: event.target.value } : current)}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 8 }}>
                <span style={{ fontWeight: 600 }}>ブランド名</span>
                <input
                  value={form.brandName}
                  onChange={(event) => setForm((current) => current ? { ...current, brandName: event.target.value } : current)}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 8 }}>
                <span style={{ fontWeight: 600 }}>タイムゾーン</span>
                <input
                  value={form.timezone}
                  onChange={(event) => setForm((current) => current ? { ...current, timezone: event.target.value } : current)}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 8 }}>
                <span style={{ fontWeight: 600 }}>公開状態</span>
                <select
                  value={form.publicStatus}
                  onChange={(event) =>
                    setForm((current) =>
                      current ? { ...current, publicStatus: event.target.value === "public" ? "public" : "private" } : current
                    )
                  }
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                >
                    <option value="private">{describeStatus("private")}</option>
                    <option value="public">{describeStatus("public")}</option>
                </select>
              </label>

              <label style={{ display: "grid", gap: 8 }}>
                <span style={{ fontWeight: 600 }}>連絡先メールアドレス</span>
                <input
                  value={form.contactEmail}
                  onChange={(event) => setForm((current) => current ? { ...current, contactEmail: event.target.value } : current)}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 8 }}>
                <span style={{ fontWeight: 600 }}>連絡先電話番号</span>
                <input
                  value={form.contactPhone}
                  onChange={(event) => setForm((current) => current ? { ...current, contactPhone: event.target.value } : current)}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 8 }}>
                <span style={{ fontWeight: 600 }}>WebサイトURL</span>
                <input
                  value={form.websiteUrl}
                  onChange={(event) => setForm((current) => current ? { ...current, websiteUrl: event.target.value } : current)}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <label style={{ display: "grid", gap: 8 }}>
                <span style={{ fontWeight: 600 }}>公開説明</span>
                <textarea
                  value={form.publicDescription}
                  onChange={(event) =>
                    setForm((current) => current ? { ...current, publicDescription: event.target.value } : current)
                  }
                  rows={4}
                  style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db" }}
                />
              </label>

              <div style={{ display: "grid", gap: 8, fontSize: 13, opacity: 0.8 }}>
                <div>URL ID は変更できません。変更が必要な場合は運営にお問い合わせください。</div>
                <div>公開スケジュール / 公開イベントの表示設定は公開設定ページで管理します。</div>
                <div>業種カテゴリは運営上の業種分類で、画面内の文言切替には使用しません。</div>
                <div>表示文言プロファイルは画面内の「レッスン・講師・スタジオ・生徒・家族アカウント・所属」などの表示名を決定します。</div>
                <div>変更が必要な場合は運営にお問い合わせください。</div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="submit" disabled={saveState === "saving"}>
                  {saveState === "saving" ? "保存中..." : "保存"}
                </button>
              </div>
            </form>

            <section style={{ display: "grid", gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>読み取り専用</h2>
              <div style={{ display: "grid", gap: 6, fontSize: 14 }}>
                <div>テナントID: {renderReadOnlyValue(settings.tenantId)}</div>
                <div>URL ID: {renderReadOnlyValue(settings.tenantSlug)}</div>
                <div>リージョン: {renderReadOnlyValue(settings.region)}</div>
                <div>ステータス: {describeStatus(settings.status)}</div>
                <div>プラン: {renderReadOnlyValue(settings.currentPlanId)}</div>
                <div>業種カテゴリ: {describeServiceCategory(settings.serviceCategory)}</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>運営上の業種分類です。画面内の文言切替には使用しません。</div>
                <div>表示文言プロファイル: {describeUiLabelProfile(settings.uiLabelProfile)}</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  表示文言プレビュー: {Object.values(getTenantLabels(settings.uiLabelProfile)).join(" / ")}
                </div>
                <div>初期設定状態: {renderReadOnlyValue(settings.setupStatus)}</div>
                <div>初期設定完了日時: {renderReadOnlyValue(settings.setupCompletedAt)}</div>
                <div>作成日時: {renderReadOnlyValue(settings.createdAt)}</div>
                <div>更新日時: {renderReadOnlyValue(settings.updatedAt)}</div>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
